import React from 'react'
import { createComponent } from '@ycos/fela'
import { BackgroundPicture } from '@ycos/picture'
import { bool, shape, number, object } from 'prop-types'
import { getFontStyles } from '@ycos/primitives'
import { fullBleedStyles } from '@ycos/layout-fullbleed'

const MOBILE_HEIGHT = '134px'
const DESKTOP_HEIGHT = '400px'

const LoyaltyBannerBox = createComponent('LoyaltyBannerBox', () => ({
  display: 'block',
  direction: 'ltr',
  ...fullBleedStyles
}))

const DesktopLayout = createComponent('DesktopLayout', () => ({
  display: 'none',
  'screen-medium': {
    display: 'block'
  }
}))

const MobileLayout = createComponent('MobileLayout', () => ({
  display: 'block',
  'screen-medium': {
    display: 'none'
  }
}))

const MobileContent = createComponent('MobileContent', ({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  padding: `0 ${theme.spacingMultiplier * 2}px`,
  boxSizing: 'border-box'
}))

const MobileContentBox = createComponent('MobileContentBox', () => ({
  textAlign: 'center',
  alignSelf: 'center'
}))

const MobileHeader = createComponent('MobileHeader', ({ theme, brand, locale }) => ({
  ...getFontStyles(theme.typography, {
    type: 'Heading',
    tagName: 'h4',
    name: brand === 'mrp' && locale.language === 'ar' ? 'A0' : 'A5'
  }),
  fontSize: theme.custom.loyaltyBanner.title.fontSizeMobile,
  fontWeight: theme.custom.loyaltyBanner.title.fontWeight,
  lineHeight: theme.custom.loyaltyBanner.title.lineHeightMobile,
  textTransform: theme.custom.loyaltyBanner.title.textTransform,
  color: theme.custom.loyaltyBanner.title.color
}))

const CtaBox = createComponent(
  'CtaBox',
  ({ theme }) => ({
    ...getFontStyles(theme.typography, {
      type: 'Label',
      tagName: 'a',
      name: '1'
    }),
    display: 'block',
    fontWeight: theme.custom.loyaltyBanner.cta.fontWeight,
    lineHeight: theme.custom.loyaltyBanner.cta.lineHeight,
    letterSpacing: theme.custom.loyaltyBanner.cta.letterSpacing,
    marginTop: `${theme.spacingMultiplier * 2}px`,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    color: theme.custom.loyaltyBanner.cta.color
  }),
  'a'
)

const DesktopContent = createComponent('DesktopContent', () => ({
  display: 'flex',
  textAlign: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%'
}))

const DesktopContentBox = createComponent('DesktopContentBox', () => ({
  alignSelf: 'center',
  width: '568px'
}))

const DesktopHeader = createComponent('DesktopHeader', ({ theme, brand, locale }) => ({
  ...getFontStyles(theme.typography, {
    type: 'Heading',
    tagName: 'h4',
    name: brand === 'mrp' && locale.language === 'ar' ? 'A0' : 'A5'
  }),
  fontSize: `${theme.custom.loyaltyBanner.title.fontSizeDesktop} !important`,
  fontWeight: `${theme.custom.loyaltyBanner.title.fontWeight} !important`,
  lineHeight: theme.custom.loyaltyBanner.title.lineHeightDesktop,
  textTransform: `${theme.custom.loyaltyBanner.title.textTransform} !important`,
  color: theme.custom.loyaltyBanner.title.color
}))

const DesktopParagraph = createComponent('DesktopParagraph', ({ theme }) => ({
  ...getFontStyles(theme.typography, {
    type: 'Paragraph',
    tagName: 'p',
    name: '3'
  }),
  fontWeight: theme.custom.loyaltyBanner.paragraph.fontWeight,
  fontSize: theme.custom.loyaltyBanner.paragraph.fontSize,
  lineHeight: theme.custom.loyaltyBanner.paragraph.lineHeight,
  marginTop: `${theme.spacingMultiplier}px`,
  color: theme.custom.loyaltyBanner.paragraph.color
}))

const LoyaltyBanner = ({ item, config, imageProps, locale, brand }) => {
  const { teaserTitle, teaserText, target, pictures } = item
  return (
    <LoyaltyBannerBox>
      <MobileLayout>
        <BackgroundPicture images={pictures} imagesphere={config.imageSphere} opacity="0.1" width="100%" height={MOBILE_HEIGHT} {...imageProps}>
          <MobileContent>
            <MobileContentBox>
              <MobileHeader brand={brand?.brandId} locale={locale}>
                {teaserTitle}
              </MobileHeader>
              <CtaBox href={target.href}>{target?.teaserTitle}</CtaBox>
            </MobileContentBox>
          </MobileContent>
        </BackgroundPicture>
      </MobileLayout>
      <DesktopLayout>
        <BackgroundPicture
          images={pictures}
          imagesphere={config.imageSphere}
          opacity="0.1"
          width="100%"
          height={DESKTOP_HEIGHT}
          {...imageProps}
          isPictureFullWidth
        >
          <DesktopContent>
            <DesktopContentBox>
              <DesktopHeader brand={brand?.brandId} locale={locale}>
                {teaserTitle}
              </DesktopHeader>
              <DesktopParagraph>{teaserText}</DesktopParagraph>
              <CtaBox href={target.href}>{target?.teaserTitle}</CtaBox>
            </DesktopContentBox>
          </DesktopContent>
        </BackgroundPicture>
      </DesktopLayout>
    </LoyaltyBannerBox>
  )
}

LoyaltyBanner.layoutVariant = 'isc-loyalty-banner'

LoyaltyBanner.propTypes = {
  config: shape({
    imagesphere: shape({
      quality: number
    })
  }),
  imageProps: shape({
    lazy: bool
  }),
  item: object
}

LoyaltyBanner.defaultProps = {
  config: {
    imageShpere: {
      quality: 65
    }
  },
  brand: {
    brandId: 'mrp'
  },
  imageProps: {
    lazy: true
  }
}

export default LoyaltyBanner
